<template>
  <base-popover 
    :alignment="alignment" 
    class="base-renewal-progress-bar"
  >
    <template #trigger="{ openPopover, closePopover }">
      <base-progress-bar
        :progress="progressPercentage"
        :green="patentIsInForce"
        class="base-renewal-progress-bar__bar"
        @mouseenter="openPopover"
        @mouseleave="closePopover"
      />
    </template>

    <template #popover>
      <base-stack
        class="base-renewal-progress-bar__status"
        direction="vertical"
        spacing="none"
      >
        <base-text-style>
          <base-subheading class="base-renewal-progress-bar__status-heading">
            Renewal progress
          </base-subheading>
        </base-text-style>

        <base-stack-item
          v-for="(step, index) in progressSteps"
          :key="index"
          :class="{
            [`base-renewal-progress-bar__status-section--state-${step.state}`]: true,
          }"
          class="base-renewal-progress-bar__status-section"
        >
          <base-stack 
            alignment="center" 
            spacing="tight" 
            no-wrap
          >
            <base-stack-item>
              <base-icon
                :icon="
                  step.state === 'pending'
                    ? 'schedule'
                    : step.state === 'processing'
                      ? 'timelapse'
                      : 'check_circle'
                "
                class="base-renewal-progress-bar__status-icon"
              />
            </base-stack-item>

            <base-stack-item>
              <p class="base-renewal-progress-bar__status-text">
                {{ step.text[step.state] }}
              </p>
            </base-stack-item>
          </base-stack>
        </base-stack-item>
      </base-stack>
    </template>
  </base-popover>
</template>

<script setup>
import { ref, computed, watch } from "vue";

const props = defineProps({
  financialStatus: {
    type: [Number],
    required: true,
  },
  alignment: {
    type: String,
    default: "bottom right",
    validator: (value) => [
      "",
      "top left",
      "top center",
      "top right",
      "right top",
      "right center",
      "right bottom",
      "bottom right",
      "bottom center",
      "bottom left",
      "left bottom",
      "left center",
      "left top",
      "custom",
      "auto",
    ].includes(value),
  },
});

const progressPercentage = ref(33);
const progressSteps = ref([
  {
    name: "Invoice sent",
    state: "completed",
    text: {
      pending: "Preparing client invoice",
      processing: "Sending client invoice",
      completed: "Invoice sent to client",
    },
  },
  {
    name: "Invoice paid",
    state: "processing",
    text: {
      pending: "Awaiting payment from client",
      processing: "Awaiting payment from client",
      completed: "Client invoice paid",
    },
  },
  {
    name: "PO paid/awaiting confirmation",
    state: "pending",
    text: {
      pending: "Payment to PO pending",
      processing: "PO paid, awaiting confirmation",
      completed: "PO has confirmed payment",
    },
  },
  {
    name: "Patent in force",
    state: "pending",
    text: {
      pending: "Patent status to be updated",
      processing: "Patent status to be updated",
      completed: "Patent in force",
    },
  },
]);

const patentIsInForce = computed(() => progressSteps.value[3].state === "completed");

const updateProgressStepStates = () => {
  if (props.financialStatus < 30) {
    progressSteps.value[0].state = "completed";
    progressSteps.value[1].state = "processing";
    progressSteps.value[2].state = "pending";
    progressSteps.value[3].state = "pending";
    progressPercentage.value = 33;
  } else if (props.financialStatus === 30) {
    progressSteps.value[0].state = "completed";
    progressSteps.value[1].state = "completed";
    progressSteps.value[2].state = "processing";
    progressSteps.value[3].state = "pending";
    progressPercentage.value = 66;
  } else {
    progressSteps.value[0].state = "completed";
    progressSteps.value[1].state = "completed";
    progressSteps.value[2].state = "completed";
    progressSteps.value[3].state = "completed";
    progressPercentage.value = 100;
  }
};

watch(() => props.financialStatus, () => {
  updateProgressStepStates();
}, { immediate: true });
</script>

<style lang="scss" scoped>
@import "./base-renewal-progress-bar.scss";
</style>
